import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTheme } from '../contexts/ThemeContext';
import type { MentionType, MentionFilters } from './chat/MentionTypes';
import {
  detectMentions as detectMentionsUtil,
  normalizeMentionValue,
  getFilterKeyForMentionType,
  isMentionComplete,
} from '../utils/mentionUtils';

interface FormattedTextInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  highlightedText?: string | null; // Texto a destacar - DEPRECATED, usar highlightedTexts
  highlightedTexts?: string[]; // Array de textos a destacar (nombres de entidades detectadas)
  highlightColor?: string; // Color para el texto destacado
  onMentionClick?: (mentionText: string, startIndex: number, endIndex: number) => void;
  onDocumentChipClick?: (documentName: string) => void; // Callback para cuando se hace click en un chip de documento (no en el X)
  activeMentionFilters?: MentionFilters; // Filtros activos válidos - solo mostrar chips para estos
  showRemoveButton?: boolean; // Si true, muestra el botón X para eliminar chips (default: true)
  knownDocumentNames?: string[]; // Nombres exactos de documentos seleccionados para validación
  className?: string;
  style?: React.CSSProperties;
}

// Helper para extraer tipo de mención del texto
// BACKWARD COMPATIBLE: Usa delimitador | para documentos (opcional para soportar formato antiguo)
const extractMentionType = (
  mentionText: string
): { type: MentionType | 'auto'; value: string; displayValue: string } => {
  // Para documentos, primero intentar con delimitador | (permite espacios)
  let documentMatch = mentionText.match(/@documento:([^|@]+)\|/);
  if (documentMatch) {
    const value = documentMatch[1].trim();
    return {
      type: 'documento' as MentionType,
      value: value,
      displayValue: value, // El nombre del documento sin el prefijo @documento: ni el delimitador |
    };
  }
  // Fallback: sin delimitador (compatibilidad hacia atrás, no permite espacios)
  documentMatch = mentionText.match(/@documento:([^|\s@]+)(?!\|)/);
  if (documentMatch) {
    const value = documentMatch[1].trim();
    return {
      type: 'documento' as MentionType,
      value: value,
      displayValue: value, // El nombre del documento sin el prefijo @documento: ni el delimitador |
    };
  }

  // Para otras menciones
  const match = mentionText.match(/@(?:(\w+):)?(.+)/);
  if (!match) return { type: 'auto', value: mentionText, displayValue: mentionText };

  const [, type, value] = match;
  const mentionType = (type as MentionType) || 'auto';

  // Display value: solo el valor sin el @type:
  const displayValue = type ? value : mentionText.replace('@', '');

  return { type: mentionType, value, displayValue };
};

// Helper para obtener colores según tipo de mención
const getChipStyles = (
  mentionType: MentionType | 'auto',
  isDark: boolean
): {
  bgColor: string;
  bgColorHover: string;
  borderColor: string;
  textColor: string;
  icon: string;
} => {
  const isDarkMode = isDark;

  // Colores por tipo (inspirados en Linear, Notion, Gmail)
  const colors = {
    empresa: {
      light: {
        bg: 'rgba(37, 99, 235, 0.1)',
        bgHover: 'rgba(37, 99, 235, 0.15)',
        border: 'rgba(37, 99, 235, 0.3)',
        text: '#2563eb',
        icon: '🏢',
      },
      dark: {
        bg: 'rgba(37, 99, 235, 0.2)',
        bgHover: 'rgba(37, 99, 235, 0.25)',
        border: 'rgba(37, 99, 235, 0.4)',
        text: '#60a5fa',
        icon: '🏢',
      },
    },
    tipo: {
      light: {
        bg: 'rgba(34, 197, 94, 0.1)',
        bgHover: 'rgba(34, 197, 94, 0.15)',
        border: 'rgba(34, 197, 94, 0.3)',
        text: '#16a34a',
        icon: '📄',
      },
      dark: {
        bg: 'rgba(34, 197, 94, 0.2)',
        bgHover: 'rgba(34, 197, 94, 0.25)',
        border: 'rgba(34, 197, 94, 0.4)',
        text: '#4ade80',
        icon: '📄',
      },
    },
    fecha: {
      light: {
        bg: 'rgba(234, 179, 8, 0.1)',
        bgHover: 'rgba(234, 179, 8, 0.15)',
        border: 'rgba(234, 179, 8, 0.3)',
        text: '#ca8a04',
        icon: '📅',
      },
      dark: {
        bg: 'rgba(234, 179, 8, 0.2)',
        bgHover: 'rgba(234, 179, 8, 0.25)',
        border: 'rgba(234, 179, 8, 0.4)',
        text: '#fbbf24',
        icon: '📅',
      },
    },
    publicado: {
      light: {
        bg: 'rgba(168, 85, 247, 0.1)',
        bgHover: 'rgba(168, 85, 247, 0.15)',
        border: 'rgba(168, 85, 247, 0.3)',
        text: '#9333ea',
        icon: '📆',
      },
      dark: {
        bg: 'rgba(168, 85, 247, 0.2)',
        bgHover: 'rgba(168, 85, 247, 0.25)',
        border: 'rgba(168, 85, 247, 0.4)',
        text: '#a78bfa',
        icon: '📆',
      },
    },
    cargado: {
      light: {
        bg: 'rgba(236, 72, 153, 0.1)',
        bgHover: 'rgba(236, 72, 153, 0.15)',
        border: 'rgba(236, 72, 153, 0.3)',
        text: '#db2777',
        icon: '⬆️',
      },
      dark: {
        bg: 'rgba(236, 72, 153, 0.2)',
        bgHover: 'rgba(236, 72, 153, 0.25)',
        border: 'rgba(236, 72, 153, 0.4)',
        text: '#f472b6',
        icon: '⬆️',
      },
    },
    codigo: {
      light: {
        bg: 'rgba(107, 114, 128, 0.1)',
        bgHover: 'rgba(107, 114, 128, 0.15)',
        border: 'rgba(107, 114, 128, 0.3)',
        text: '#4b5563',
        icon: '🔢',
      },
      dark: {
        bg: 'rgba(107, 114, 128, 0.2)',
        bgHover: 'rgba(107, 114, 128, 0.25)',
        border: 'rgba(107, 114, 128, 0.4)',
        text: '#9ca3af',
        icon: '🔢',
      },
    },
    archivo: {
      light: {
        bg: 'rgba(245, 158, 11, 0.1)',
        bgHover: 'rgba(245, 158, 11, 0.15)',
        border: 'rgba(245, 158, 11, 0.3)',
        text: '#d97706',
        icon: '📎',
      },
      dark: {
        bg: 'rgba(245, 158, 11, 0.2)',
        bgHover: 'rgba(245, 158, 11, 0.25)',
        border: 'rgba(245, 158, 11, 0.4)',
        text: '#fbbf24',
        icon: '📎',
      },
    },
    sector: {
      light: {
        bg: 'rgba(168, 85, 247, 0.1)',
        bgHover: 'rgba(168, 85, 247, 0.15)',
        border: 'rgba(168, 85, 247, 0.3)',
        text: '#9333ea',
        icon: '🏭',
      },
      dark: {
        bg: 'rgba(168, 85, 247, 0.2)',
        bgHover: 'rgba(168, 85, 247, 0.25)',
        border: 'rgba(168, 85, 247, 0.4)',
        text: '#a78bfa',
        icon: '🏭',
      },
    },
    documento: {
      light: {
        bg: 'rgba(139, 142, 230, 0.08)',
        bgHover: 'rgba(139, 142, 230, 0.12)',
        border: 'rgba(139, 142, 230, 0.2)',
        text: '#818cf8',
        icon: '📄',
      },
      dark: {
        bg: 'rgba(139, 142, 230, 0.15)',
        bgHover: 'rgba(139, 142, 230, 0.2)',
        border: 'rgba(139, 142, 230, 0.3)',
        text: '#a5b4fc',
        icon: '📄',
      },
    },
    auto: {
      light: {
        bg: 'rgba(37, 99, 235, 0.1)',
        bgHover: 'rgba(37, 99, 235, 0.15)',
        border: 'rgba(37, 99, 235, 0.3)',
        text: '#2563eb',
        icon: '🏢',
      },
      dark: {
        bg: 'rgba(37, 99, 235, 0.2)',
        bgHover: 'rgba(37, 99, 235, 0.25)',
        border: 'rgba(37, 99, 235, 0.4)',
        text: '#60a5fa',
        icon: '🏢',
      },
    },
  };

  const theme = isDarkMode ? 'dark' : 'light';
  const typeColors = colors[mentionType] || colors.auto;

  return {
    bgColor: typeColors[theme].bg,
    bgColorHover: typeColors[theme].bgHover,
    borderColor: typeColors[theme].border,
    textColor: typeColors[theme].text,
    icon: typeColors[theme].icon,
  };
};

export interface FormattedTextInputHandle {
  setCursorToEnd: () => void;
  setCursorPosition: (position: number) => void;
  focus: () => void;
}

/**
 * Componente de input con formato usando ContentEditable
 * Permite texto con formato sin usar overlay
 *
 * Mejores prácticas:
 * - Usa ContentEditable nativo del navegador
 * - Mantiene el cursor y selección correctamente
 * - Soporta múltiples formatos
 * - Accesible y ligero
 */
const FormattedTextInput = forwardRef<FormattedTextInputHandle, FormattedTextInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      placeholder,
      disabled = false,
      highlightedText = null, // DEPRECATED
      highlightedTexts = [],
      highlightColor = 'var(--nb-accent)',
      onMentionClick,
      onDocumentChipClick,
      activeMentionFilters = {},
      showRemoveButton = true, // Por defecto se muestra el botón X para eliminar chips
      knownDocumentNames = [], // Nombres exactos de documentos seleccionados para validación
      className = '',
      style = {},
    },
    ref
  ) => {
    // Compatibilidad con highlightedText (deprecated)
    const companiesToHighlight =
      highlightedTexts.length > 0 ? highlightedTexts : highlightedText ? [highlightedText] : [];
    const { theme } = useTheme();
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const isTypingRef = useRef(false); // Rastrear si el usuario está escribiendo activamente
    const lastCursorPositionRef = useRef<{ node: Node | null; offset: number } | null>(null);
    const lastPlainTextRef = useRef<string>(''); // Guardar el texto anterior (sin formato) para detectar nuevos chips
    const isMountedRef = useRef(false); // Flag para verificar si el componente está montado

    // Ref para mantener siempre la versión más reciente del callback onMentionClick
    // Esto es necesario porque los event listeners de los chips pueden capturar versiones viejas del callback
    const onMentionClickRef = useRef(onMentionClick);
    onMentionClickRef.current = onMentionClick;

    // Ref para mantener siempre la versión más reciente del callback onDocumentChipClick
    const onDocumentChipClickRef = useRef(onDocumentChipClick);
    onDocumentChipClickRef.current = onDocumentChipClick;

    // Función para guardar la posición del cursor de manera precisa
    const saveCursorPosition = useCallback(() => {
      if (!contentEditableRef.current) return null;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return null;

      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(contentEditableRef.current);
      preCaretRange.setEnd(range.startContainer, range.startOffset);

      // Guardar posición basada en texto (más robusta)
      const textBeforeCursor = preCaretRange.toString();
      return {
        textBeforeCursor,
        textLength: textBeforeCursor.length,
        node: range.startContainer,
        offset: range.startOffset,
      };
    }, []);

    // Función para restaurar la posición del cursor de manera precisa
    const restoreCursorPosition = useCallback(
      (savedPosition: { textBeforeCursor: string; textLength: number } | null) => {
        if (!contentEditableRef.current || !savedPosition) return;

        const selection = window.getSelection();
        if (!selection) return;

        // Buscar la posición basada en el texto antes del cursor
        let currentPos = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        const walker = document.createTreeWalker(
          contentEditableRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node: Node | null;
        while ((node = walker.nextNode())) {
          const nodeText = node.textContent || '';
          const nodeLength = nodeText.length;

          if (currentPos + nodeLength >= savedPosition.textLength) {
            targetNode = node;
            targetOffset = savedPosition.textLength - currentPos;
            break;
          }
          currentPos += nodeLength;
        }

        if (targetNode) {
          try {
            const range = document.createRange();
            range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            // Si falla, poner el cursor al final
            const range = document.createRange();
            range.selectNodeContents(contentEditableRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          // Si no encontramos la posición, poner el cursor al final
          const range = document.createRange();
          range.selectNodeContents(contentEditableRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      },
      []
    );

    // Función para posicionar el cursor al final del contenido
    const setCursorToEnd = useCallback(() => {
      if (!contentEditableRef.current) return;

      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(contentEditableRef.current);
      range.collapse(false); // false = al final
      selection.removeAllRanges();
      selection.addRange(range);

      // Asegurar que el elemento tenga foco
      contentEditableRef.current.focus();
    }, []);

    // Función para posicionar el cursor en una posición específica
    const setCursorPosition = useCallback(
      (position: number) => {
        if (!contentEditableRef.current) return;

        const selection = window.getSelection();
        if (!selection) return;

        // Buscar el nodo y offset correspondiente a la posición
        let currentPos = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        const walker = document.createTreeWalker(
          contentEditableRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node: Node | null;
        while ((node = walker.nextNode())) {
          const nodeText = node.textContent || '';
          const nodeLength = nodeText.length;

          if (currentPos + nodeLength >= position) {
            targetNode = node;
            targetOffset = position - currentPos;
            break;
          }
          currentPos += nodeLength;
        }

        if (targetNode) {
          try {
            const range = document.createRange();
            range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            // Si falla, poner el cursor al final
            setCursorToEnd();
          }
        } else {
          // Si no encontramos la posición, poner el cursor al final
          setCursorToEnd();
        }

        // Asegurar que el elemento tenga foco
        contentEditableRef.current.focus();
      },
      [setCursorToEnd]
    );

    // Función para hacer focus en el input
    const focus = useCallback(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.focus();
      }
    }, []);

    // Helper para verificar si un valor de mención es válido (está en activeMentionFilters)
    const isMentionValueValid = useCallback(
      (mentionType: MentionType | 'auto', mentionValue: string): boolean => {
        // Para menciones de tipo 'documento', siempre son válidas (no requieren filtros activos)
        if (mentionType === 'documento') {
          return true;
        }

        const filterKey = getFilterKeyForMentionType(mentionType);
        if (!filterKey) return false;

        const filterValue = activeMentionFilters[filterKey];
        if (!filterValue) return false;

        const normalizedValue = normalizeMentionValue(mentionValue);
        const values = Array.isArray(filterValue) ? filterValue : [filterValue];
        return values.some((v) => normalizeMentionValue(v) === normalizedValue);
      },
      [activeMentionFilters]
    );

    // Función para extraer texto del contentEditable preservando el formato original de las menciones
    // Los chips se renderizan como "empresa: VALOR" pero el texto original es "@empresa:VALOR"
    // Esta función reconstruye el formato original para que las menciones se detecten correctamente
    const extractTextWithMentions = useCallback((): string => {
      if (!contentEditableRef.current) return '';

      let result = '';

      const processNode = (node: Node): void => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Nodo de texto normal
          result += node.textContent || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;

          // Verificar si es un chip de mención
          if (element.classList.contains('mention-filter-chip')) {
            // Extraer el tipo y valor del chip y reconstruir el formato original
            const mentionType = element.getAttribute('data-mention-type') || 'empresa';
            const mentionValue = element.getAttribute('data-mention-value') || '';

            if (mentionValue) {
              // Reconstruir el formato original: @tipo:valor
              // Para documentos, agregar delimitador | después del nombre
              if (mentionType === 'documento') {
                result += `@documento:${mentionValue}|`;
              } else {
                result += `@${mentionType}:${mentionValue}`;
              }
            }
            // No procesar hijos del chip (evita leer el prefijo @ y el texto por separado)
          } else {
            // Procesar hijos recursivamente solo si no es un chip
            node.childNodes.forEach((child) => processNode(child));
          }
        }
      };

      contentEditableRef.current.childNodes.forEach((child) => processNode(child));

      return result;
    }, []);

    // Marcar el componente como montado después del primer render
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Detectar menciones en el texto - solo mostrar chips cuando el filtro está COMPLETO y el VALOR ES VÁLIDO
    const detectMentions = useCallback(
      (text: string): Array<{ text: string; startIndex: number; endIndex: number }> => {
        const parsedMentions = detectMentionsUtil(text, knownDocumentNames);
        const mentions: Array<{ text: string; startIndex: number; endIndex: number }> = [];

        for (const mention of parsedMentions) {
          const mentionValue = mention.value.trim();

          // Solo incluir si tiene valor válido
          if (!mentionValue) continue;

          // Para documentos, siempre considerarlos completos (vienen del selector con nombre exacto)
          // Para otros tipos, verificar si están completos
          const isComplete =
            mention.type === 'documento'
              ? true
              : isMentionComplete(mention.endIndex, text.length, text, mentionValue);
          if (!isComplete) continue;

          const isValidValue = isMentionValueValid(mention.type, mentionValue);
          if (!isValidValue) continue;

          // Evitar duplicados
          const isDuplicate = mentions.some(
            (m) => m.startIndex === mention.startIndex && m.endIndex === mention.endIndex
          );

          if (!isDuplicate) {
            mentions.push({
              text: mention.fullText,
              startIndex: mention.startIndex,
              endIndex: mention.endIndex,
            });
          }
        }

        mentions.sort((a, b) => a.startIndex - b.startIndex);
        return mentions;
      },
      [isMentionValueValid, knownDocumentNames]
    );

    // Función para actualizar el contenido con formato
    const updateContent = useCallback(
      (text: string, preserveCursor: boolean = true, forceCursorToEnd: boolean = false) => {
        if (!contentEditableRef.current) return;

        // Detectar menciones
        const mentions = detectMentions(text);
        const hasMentions = mentions.length > 0;
        const hasHighlights = companiesToHighlight.length > 0 && text.trim();

        // Verificar si se agregó una nueva mención completa (nuevo chip)
        // Esto ocurre cuando el usuario acaba de terminar de escribir un filtro
        const previousMentions = detectMentions(lastPlainTextRef.current);
        const mentionsCountIncreased = mentions.length > previousMentions.length;
        const isNewMentionComplete = mentionsCountIncreased && !isTypingRef.current;

        // Si se fuerza el cursor al final o se detectó un nuevo chip, no guardar posición
        const shouldForceCursorToEnd = forceCursorToEnd || isNewMentionComplete;
        const savedPosition =
          preserveCursor && !isTypingRef.current && !shouldForceCursorToEnd
            ? saveCursorPosition()
            : null;

        // Si no hay menciones ni highlights, mostrar texto plano
        if (!hasMentions && !hasHighlights) {
          contentEditableRef.current.textContent = text;
          if (savedPosition) {
            restoreCursorPosition(savedPosition);
          } else if (shouldForceCursorToEnd) {
            setTimeout(() => setCursorToEnd(), 0);
          }
          return;
        }

        // Limpiar contenido anterior
        contentEditableRef.current.innerHTML = '';

        // Si hay menciones, renderizar con menciones interactivas como chips visuales
        if (hasMentions) {
          let currentIndex = 0;
          const isDark = theme.mode === 'dark';

          mentions.forEach((mention, mentionIndex) => {
            // Extraer tipo y valor de la mención primero para saber si es documento
            const { type, displayValue, value: mentionValue } = extractMentionType(mention.text);

            // Agregar texto antes de la mención
            const textStartIndex = currentIndex;
            const textEndIndex = mention.startIndex;

            if (textEndIndex > textStartIndex) {
              const beforeText = text.substring(textStartIndex, textEndIndex);
              if (beforeText) {
                const textNode = document.createTextNode(beforeText);
                contentEditableRef.current?.appendChild(textNode);
              }
            }

            const chipStyles = getChipStyles(type, isDark);

            // Obtener etiqueta del tipo de filtro
            const getTypeLabel = (mentionType: MentionType | 'auto'): string => {
              const labels: Record<string, string> = {
                empresa: 'empresa',
                tipo: 'tipo',
                fecha: 'fecha',
                documento: 'documento',
                auto: 'empresa',
              };
              return labels[mentionType] || 'filtro';
            };

            const typeLabel = getTypeLabel(type);
            // Para documentos, mostrar solo el nombre sin el prefijo "documento:"
            // Pero incluir el @ al inicio del chip
            // Asegurar que displayValue no incluya el @ (por si acaso)
            const cleanDisplayValue = displayValue.replace(/^@+/, '').trim();
            const chipText =
              type === 'documento' ? cleanDisplayValue : `${typeLabel}: ${displayValue}`;
            const chipPrefix = type === 'documento' ? '@' : '';

            // Crear contenedor del chip con estilos visuales similares al diseño mostrado
            // Para documentos, usar los colores específicos de chipStyles (indigo/púrpura)
            // Para otros tipos, usar el estilo genérico gris
            const mentionSpan = document.createElement('span');
            const bgColor =
              type === 'documento'
                ? chipStyles.bgColor
                : isDark
                  ? 'rgba(55, 65, 81, 0.8)'
                  : 'rgba(229, 231, 235, 0.8)';
            const textColor =
              type === 'documento' ? chipStyles.textColor : isDark ? '#e5e7eb' : '#374151';
            const borderColor = type === 'documento' ? chipStyles.borderColor : 'transparent';

            mentionSpan.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          margin: 0 2px;
          border-radius: 6px;
          background-color: ${bgColor};
          color: ${textColor};
          font-weight: 400;
          font-size: 13px;
          line-height: 1.4;
          cursor: default;
          transition: background-color 0.15s ease;
          vertical-align: middle;
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          border: ${type === 'documento' ? `1px solid ${borderColor}` : 'none'};
          max-width: 100%;
          flex-wrap: wrap;
          word-wrap: break-word;
          overflow-wrap: break-word;
        `;
            // Permitir que el cursor se posicione después del chip
            mentionSpan.setAttribute('contenteditable', 'false');
            mentionSpan.className = 'mention-filter-chip';
            mentionSpan.setAttribute('data-mention-start', mention.startIndex.toString());
            mentionSpan.setAttribute('data-mention-end', mention.endIndex.toString());
            mentionSpan.setAttribute('data-mention-type', type);
            mentionSpan.setAttribute('data-mention-value', mentionValue);

            // Icono del tipo de mención (a la izquierda)
            const iconSpan = document.createElement('span');
            iconSpan.textContent = chipStyles.icon;
            iconSpan.style.cssText = `
          font-size: 14px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
          opacity: 0.8;
        `;
            mentionSpan.appendChild(iconSpan);

            // Prefijo @ para documentos (visible en el chip)
            if (chipPrefix) {
              const prefixNode = document.createElement('span');
              prefixNode.textContent = chipPrefix;
              prefixNode.style.cssText = `
            display: inline;
            font-weight: 500;
            color: ${chipStyles.textColor};
            margin-right: 2px;
          `;
              mentionSpan.appendChild(prefixNode);
            }

            // Texto del chip: "tipo: valor" o solo "valor" para documentos (en el centro)
            const mentionTextNode = document.createElement('span');
            mentionTextNode.textContent = chipText;
            mentionTextNode.style.cssText = `
          display: inline-block;
          font-weight: 400;
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
          flex: 1;
          min-width: 0;
          color: ${textColor};
        `;
            mentionSpan.appendChild(mentionTextNode);

            // Botón X para eliminar (a la derecha, similar al diseño mostrado)
            // Solo mostrar si showRemoveButton es true y hay un callback para manejar el click
            if (onMentionClick && showRemoveButton) {
              const removeButton = document.createElement('button');
              removeButton.innerHTML = '×';
              removeButton.style.cssText = `
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            padding: 0;
            margin: 0;
            margin-left: 2px;
            cursor: pointer;
            color: ${isDark ? '#9ca3af' : '#6b7280'};
            font-size: 16px;
            font-weight: 400;
            line-height: 1;
            width: 16px;
            height: 16px;
            min-width: 16px;
            min-height: 16px;
            border-radius: 2px;
            opacity: 0.8;
            transition: opacity 0.15s ease, background-color 0.15s ease;
            flex-shrink: 0;
            outline: none;
          `;
              removeButton.className = 'mention-remove';
              removeButton.setAttribute('aria-label', 'Eliminar filtro');
              removeButton.setAttribute('tabindex', '-1');
              removeButton.setAttribute('type', 'button');

              // Habilitar pointer events solo para el botón X
              removeButton.style.pointerEvents = 'auto';

              // Handler para click en el botón X
              // Usamos onMentionClickRef.current para siempre tener la versión más reciente del callback
              // Esto evita que los chips usen versiones viejas del callback cuando el texto cambia
              removeButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                if (onMentionClickRef.current) {
                  onMentionClickRef.current(mention.text, mention.startIndex, mention.endIndex);
                }
              });

              // Hover en el botón X - hacer más visible
              removeButton.addEventListener('mouseenter', () => {
                removeButton.style.opacity = '1';
                removeButton.style.color = isDark ? '#e5e7eb' : '#374151';
              });

              removeButton.addEventListener('mouseleave', () => {
                removeButton.style.opacity = '0.8';
                removeButton.style.color = isDark ? '#9ca3af' : '#6b7280';
              });

              // También manejar mousedown para mejor feedback
              removeButton.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
              });

              mentionSpan.appendChild(removeButton);
            }

            // Hover effect en el chip completo - hacer X más visible al hover
            mentionSpan.addEventListener('mouseenter', () => {
              mentionSpan.style.backgroundColor = isDark
                ? 'rgba(75, 85, 99, 0.9)'
                : 'rgba(209, 213, 219, 0.9)';
              const removeBtn = mentionSpan.querySelector('.mention-remove') as HTMLElement;
              if (removeBtn) {
                removeBtn.style.opacity = '1';
              }
            });

            mentionSpan.addEventListener('mouseleave', () => {
              mentionSpan.style.backgroundColor = isDark
                ? 'rgba(55, 65, 81, 0.8)'
                : 'rgba(229, 231, 235, 0.8)';
              const removeBtn = mentionSpan.querySelector('.mention-remove') as HTMLElement;
              if (removeBtn) {
                removeBtn.style.opacity = '0.8';
                removeBtn.style.color = isDark ? '#9ca3af' : '#6b7280';
              }
            });

            // Handler para click en el chip de documento (no en el botón X)
            // Solo para chips de tipo 'documento'
            if (type === 'documento' && onDocumentChipClickRef.current) {
              mentionSpan.style.cursor = 'pointer';
              mentionSpan.addEventListener('click', (e) => {
                // No activar si el click fue en el botón X
                const target = e.target as HTMLElement;
                if (target.closest('.mention-remove')) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                if (onDocumentChipClickRef.current) {
                  onDocumentChipClickRef.current(mentionValue);
                }
              });
            }

            contentEditableRef.current?.appendChild(mentionSpan);
            currentIndex = mention.endIndex;
          });

          // Agregar texto después de la última mención
          if (currentIndex < text.length) {
            const afterText = text.substring(currentIndex);
            if (afterText) {
              const textNode = document.createTextNode(afterText);
              contentEditableRef.current?.appendChild(textNode);
            }
          }
        } else if (hasHighlights) {
          // Si no hay menciones pero hay highlights, usar la lógica original
          const words = text.split(/(\s+)/);

          const allEntityWords = new Set<string>();
          companiesToHighlight.forEach((entity) => {
            const entityWords = entity
              .toLowerCase()
              .split(/\s+/)
              .filter((w) => w.length >= 3);
            entityWords.forEach((ew) => allEntityWords.add(ew));
          });

          words.forEach((word) => {
            const span = document.createElement('span');
            const wordTrimmed = word.trim().toLowerCase();

            if (!wordTrimmed) {
              span.textContent = word;
            } else if (wordTrimmed.length < 3) {
              span.textContent = word;
            } else {
              let isPartOfHighlight = false;

              if (allEntityWords.has(wordTrimmed)) {
                isPartOfHighlight = true;
              } else {
                for (const entityWord of allEntityWords) {
                  if (entityWord === wordTrimmed) {
                    isPartOfHighlight = true;
                    break;
                  }
                  if (wordTrimmed.length >= 4 && entityWord.startsWith(wordTrimmed)) {
                    isPartOfHighlight = true;
                    break;
                  }
                  if (entityWord.length >= 4 && wordTrimmed.startsWith(entityWord)) {
                    isPartOfHighlight = true;
                    break;
                  }
                }
              }

              if (isPartOfHighlight) {
                span.style.color = highlightColor;
                span.style.fontWeight = '500';
              }
              span.textContent = word;
            }

            contentEditableRef.current?.appendChild(span);
          });
        }

        // Actualizar el texto anterior después de renderizar
        lastPlainTextRef.current = text;

        // Restaurar cursor si se guardó, o posicionar al final si se fuerza o se detectó nuevo chip
        if (shouldForceCursorToEnd || forceCursorToEnd) {
          // Cuando se crea un nuevo chip, posicionar cursor al final para continuar escribiendo
          setTimeout(() => {
            setCursorToEnd();
          }, 0);
        } else if (savedPosition) {
          restoreCursorPosition(savedPosition);
        }
      },
      [
        companiesToHighlight,
        highlightColor,
        saveCursorPosition,
        restoreCursorPosition,
        setCursorToEnd,
        detectMentions,
        onMentionClick,
        showRemoveButton,
        theme,
      ]
    );

    // Actualizar el contenido cuando cambia el value externamente
    useEffect(() => {
      // No ejecutar si el componente no está montado o el usuario está escribiendo
      if (!isMountedRef.current || !contentEditableRef.current || isTypingRef.current) {
        return;
      }

      try {
        // Extraer texto actual del DOM
        const currentText = extractTextWithMentions();

        // Si el texto actual coincide con el value, no hacer nada (usuario está escribiendo normalmente)
        if (currentText === value) {
          return;
        }

        // El value cambió externamente - actualizar el contenido
        updateContent(value, true);
      } catch (error) {
        // Silenciar errores durante la inicialización
        if (isMountedRef.current) {
          console.error('Error updating FormattedTextInput:', error);
        }
      }
    }, [value, updateContent, extractTextWithMentions]);

    // Store typing timeout ref to clear it if needed
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Manejar cambios en el contenido
    const handleInput = useCallback(() => {
      if (!contentEditableRef.current) return;

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Marcar que el usuario está escribiendo
      isTypingRef.current = true;

      // IMPORTANTE: Extraer texto preservando el formato original de las menciones
      // Esto evita que los chips desaparezcan cuando el usuario edita el texto
      const text = extractTextWithMentions();
      onChange(text);

      // CRITICAL: Don't update content while user is typing
      // Only clear typing flag after a delay to prevent useEffect from interfering
      // Don't re-apply formatting while typing - let user finish first
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;

        // Re-extraer el texto actual (podría haber cambiado mientras escribía)
        const currentText = extractTextWithMentions();

        // Re-apply formatting if there are highlights or mentions and user stopped typing
        const mentions = detectMentions(currentText);
        const hasMentions = mentions.length > 0;
        const hasHighlights = companiesToHighlight.length > 0 && currentText.trim();

        if ((hasHighlights || hasMentions) && contentEditableRef.current) {
          // Verificar si se agregó una nueva mención completa (nuevo chip)
          const previousMentions = detectMentions(lastPlainTextRef.current);
          const newMentionDetected = mentions.length > previousMentions.length;
          updateContent(currentText, true, newMentionDetected);
          // Actualizar el texto anterior después de procesar
          lastPlainTextRef.current = currentText;
        }
      }, 300); // Longer delay to ensure user has stopped typing
    }, [onChange, companiesToHighlight, updateContent, detectMentions, extractTextWithMentions]);

    // Manejar teclas especiales
    const handleKeyDownInternal = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Permitir que el componente padre maneje la tecla si es necesario
        if (onKeyDown) {
          onKeyDown(e);
        }

        // Prevenir Enter si no se maneja en el padre
        if (e.key === 'Enter' && !e.shiftKey && !onKeyDown) {
          e.preventDefault();
        }
      },
      [onKeyDown]
    );

    // Manejar paste para mantener solo texto plano
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    }, []);

    // Exponer funciones para posicionar cursor
    useImperativeHandle(
      ref,
      () => ({
        setCursorToEnd,
        setCursorPosition,
        focus,
      }),
      [setCursorToEnd, setCursorPosition, focus]
    );

    return (
      <>
        <style>{`
        [data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: ${theme.colors.text.secondary};
          pointer-events: none;
        }
      `}</style>
        <div
          ref={contentEditableRef}
          id="chat-message-input"
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDownInternal}
          onPaste={handlePaste}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          suppressContentEditableWarning
          className={className}
          style={{
            minHeight: '52px',
            maxHeight: '160px',
            overflowY: 'auto',
            outline: 'none',
            border: 'none',
            padding: '12px 16px',
            fontSize: '14px',
            lineHeight: '1.5',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            scrollbarWidth: 'thin',
            caretColor: theme.colors.text.primary,
            color: theme.colors.text.primary,
            ...style,
          }}
          data-placeholder={!value && !isFocused ? placeholder : ''}
          role="textbox"
          aria-label={placeholder}
          aria-multiline="true"
          aria-disabled={disabled}
        />
      </>
    );
  }
);

FormattedTextInput.displayName = 'FormattedTextInput';

export default FormattedTextInput;
